// ─────────────────────────────────────────────────────
// FRONT PAGE JS
// ─────────────────────────────────────────────────────
import Swiper from 'swiper';
import { Navigation, Pagination, Autoplay, EffectFade } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';

function ensureSwiperAutoplay(selector, delay) {
  const el = document.querySelector(selector);
  if (!el || el.dataset.autoplayFallbackReady === '1') return;

  const attach = () => {
    const instance = el.swiper;
    if (!instance) {
      window.setTimeout(attach, 250);
      return;
    }

    el.dataset.autoplayFallbackReady = '1';

    if (instance.autoplay && typeof instance.autoplay.start === 'function') {
      instance.params.autoplay = {
        ...(instance.params.autoplay || {}),
        delay,
        disableOnInteraction: false,
      };
      instance.autoplay.start();
      return;
    }

    window.setInterval(() => {
      if (document.hidden || instance.destroyed || instance.animating) return;
      instance.slideNext();
    }, delay);
  };

  attach();
}

function initFrontPage() {

  // ── Hero swiper ─────────────────────────────────────
  if (document.querySelector('.fp-hero-swiper')) {
    const heroSwiper = new Swiper('.fp-hero-swiper', {
      slidesPerView: 1,
      loop: true,
      speed: 1200,
      autoplay: { delay: 5200, disableOnInteraction: false, pauseOnMouseEnter: false },
      pagination: { el: '.fp-hero-pagination', clickable: true, type: 'bullets' },
      navigation: { nextEl: '.fp-hero-next', prevEl: '.fp-hero-prev' },
      effect: 'fade',
      fadeEffect: { crossFade: true },
      uniqueNavElements: false,
      loopedSlides: 3,
    });

    // Fallback manual — por si Swiper no conecta los botones automáticamente
    const prevBtn = document.querySelector('.fp-hero-prev');
    const nextBtn = document.querySelector('.fp-hero-next');
    if (prevBtn) prevBtn.addEventListener('click', () => heroSwiper.slidePrev());
    if (nextBtn) nextBtn.addEventListener('click', () => heroSwiper.slideNext());
    ensureSwiperAutoplay('.fp-hero-swiper', 5200);
  }

  // ── Stops carousel ──────────────────────────────────
  if (document.querySelector('.fp-stops-swiper')) {

    const stopsSection = document.querySelector('.fp-stops--fullbg');

    function updateStopsBg(swiper) {
      const activeSlide = swiper.slides[swiper.activeIndex];
      if (activeSlide && stopsSection) {
        const bg = activeSlide.dataset.bg;
        if (bg) stopsSection.style.setProperty('--stops-bg', "url('" + bg + "')");
      }
    }

    const firstSlide = document.querySelector('.fp-stop__slide[data-bg]');
    if (firstSlide && stopsSection) {
      stopsSection.style.setProperty('--stops-bg', "url('" + firstSlide.dataset.bg + "')");
    }

    const stopsSwiper = new Swiper('.fp-stops-swiper', {
      slidesPerView: 1,
      loop: true,
      speed: 1000,
      effect: 'fade',
      fadeEffect: { crossFade: true },
      autoplay: { delay: 5000, disableOnInteraction: false, pauseOnMouseEnter: false },
      pagination: { el: '.fp-stops-pagination', clickable: true },
      navigation: { nextEl: '.fp-stops-next', prevEl: '.fp-stops-prev' },
      on: { slideChange: updateStopsBg },
    });

    const stopsPrev = document.querySelector('.fp-stops-prev');
    const stopsNext = document.querySelector('.fp-stops-next');
    if (stopsPrev) stopsPrev.addEventListener('click', () => stopsSwiper.slidePrev());
    if (stopsNext) stopsNext.addEventListener('click', () => stopsSwiper.slideNext());
    ensureSwiperAutoplay('.fp-stops-swiper', 5000);
  }

  // ── Tours swiper ────────────────────────────────────
  if (document.querySelector('.fp-tours-swiper')) {
    const toursSwiper = new Swiper('.fp-tours-swiper', {
      slidesPerView: 1,
      spaceBetween: 24,
      loop: true,
      speed: 600,
      autoplay: { delay: 4600, disableOnInteraction: false, pauseOnMouseEnter: false },
      pagination: { el: '.fp-tours-pagination', clickable: true, dynamicBullets: true },
      navigation: { nextEl: '.fp-tours-next', prevEl: '.fp-tours-prev' },
      breakpoints: {
        640:  { slidesPerView: 2, spaceBetween: 20 },
        1024: { slidesPerView: 3, spaceBetween: 24 },
      },
    });

    const toursPrev = document.querySelector('.fp-tours-prev');
    const toursNext = document.querySelector('.fp-tours-next');
    if (toursPrev) toursPrev.addEventListener('click', () => toursSwiper.slidePrev());
    if (toursNext) toursNext.addEventListener('click', () => toursSwiper.slideNext());
    ensureSwiperAutoplay('.fp-tours-swiper', 4600);
  }

  // ── Combos swiper ───────────────────────────────────
  if (document.querySelector('.fp-combos-swiper')) {
    const combosSwiper = new Swiper('.fp-combos-swiper', {
      slidesPerView: 1,
      spaceBetween: 24,
      loop: true,
      speed: 600,
      autoplay: { delay: 5000, disableOnInteraction: false, pauseOnMouseEnter: false },
      pagination: { el: '.fp-combos-pagination', clickable: true, dynamicBullets: true },
      navigation: { nextEl: '.fp-combos-next', prevEl: '.fp-combos-prev' },
      breakpoints: {
        640:  { slidesPerView: 2, spaceBetween: 20 },
        1024: { slidesPerView: 3, spaceBetween: 24 },
      },
    });

    const combosPrev = document.querySelector('.fp-combos-prev');
    const combosNext = document.querySelector('.fp-combos-next');
    if (combosPrev) combosPrev.addEventListener('click', () => combosSwiper.slidePrev());
    if (combosNext) combosNext.addEventListener('click', () => combosSwiper.slideNext());
    ensureSwiperAutoplay('.fp-combos-swiper', 5000);
  }

  // ── Reviews swiper ──────────────────────────────────
  if (document.querySelector('.fp-reviews-swiper')) {
    const reviewsSwiper = new Swiper('.fp-reviews-swiper', {
      slidesPerView: 1,
      spaceBetween: 20,
      loop: true,
      speed: 600,
      autoplay: { delay: 5600, disableOnInteraction: false, pauseOnMouseEnter: false },
      pagination: { el: '.fp-reviews-pagination', clickable: true, dynamicBullets: true },
      navigation: { nextEl: '.fp-reviews-next', prevEl: '.fp-reviews-prev' },
      breakpoints: {
        768: { slidesPerView: 2 },
      },
    });

    const reviewsPrev = document.querySelector('.fp-reviews-prev');
    const reviewsNext = document.querySelector('.fp-reviews-next');
    if (reviewsPrev) reviewsPrev.addEventListener('click', () => reviewsSwiper.slidePrev());
    if (reviewsNext) reviewsNext.addEventListener('click', () => reviewsSwiper.slideNext());
    ensureSwiperAutoplay('.fp-reviews-swiper', 5600);
  }

}

// Ejecutar cuando el DOM esté listo
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initFrontPage);
} else {
  initFrontPage();
}

// ── Animaciones de scroll — Intersection Observer ──
